import { Instagram, Mail, MapPin, MessageCircle, Play, Tv, Users, Youtube, Megaphone, Gift, Handshake } from "lucide-react";
import { AUDIENCE, CITY, CONTACT, FAMILY, HIGHLIGHTS, IG_CHANNELS, LOGOS, PHOTOS, PARTNERSHIP, SOCIAL, TOTALS, YT_CHANNELS } from "@/data/content";
import { Note, Star } from "@/components/site/Reveal";
import { Slide, SlideTitle } from "./Slide";

export const Cover = (p) => (
  <Slide {...p} testid="slide-capa">
    <div className="pointer-events-none absolute -left-40 top-10 h-[26rem] w-[26rem] rounded-full bg-[var(--blue)]/15 blur-3xl" />
    <div className="pointer-events-none absolute -right-28 bottom-0 h-[24rem] w-[24rem] rounded-full bg-[var(--green)]/15 blur-3xl" />
    <Star className="absolute left-[46%] top-12 h-12 w-12" />
    <Note className="absolute right-20 top-16 h-12 w-12" fill="var(--green)" />
    <div className="grid h-full grid-cols-12 items-center gap-10">
      <div className="col-span-7">
        <span className="font-mono-label mb-6 inline-flex -rotate-2 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white">
          <span className="h-2 w-2 rounded-full bg-[var(--yellow)]" /> Conteúdo familiar & infantil
        </span>
        <h1 className="text-[5.6rem] leading-[0.95]">
          <span className="puffy puffy-blue block">Música,</span>
          <span className="puffy puffy-green block">aventura e</span>
          <span className="puffy puffy-red block">família.</span>
        </h1>
        <p className="mt-7 max-w-md text-lg text-[var(--ink)]/75">
          Somos Diego, Dani e o Pedro, de Porto Alegre. Criamos memórias, músicas e entretenimento seguro, autêntico e de alta qualidade para toda a família.
        </p>
      </div>
      <div className="col-span-5">
        <img src={LOGOS.show} alt="Pedro Clube Show" className="w-full -rotate-3 drop-shadow-[0_30px_40px_rgba(31,63,151,0.35)]" />
      </div>
    </div>
  </Slide>
);

export const Numbers = (p) => (
  <Slide {...p} bg="bg-[var(--blue-deep)]" dark testid="slide-numeros">
    <Star className="absolute right-[8%] top-12 h-16 w-16" />
    <SlideTitle kicker="Mídia kit" dark>
      Números que <span className="text-[var(--yellow)]">brilham</span>
      <br />
      na sala de estar.
    </SlideTitle>
    <span className="font-mono-label mt-8 inline-flex w-fit rotate-1 items-center gap-2 rounded-full bg-[var(--yellow)] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.18em] text-[var(--ink)] shadow-[0_4px_0_#c58f0a]">
      <Star className="h-4 w-4" fill="var(--red)" /> Tudo isso em apenas 4 meses
    </span>
    <div className="mt-8 grid grid-cols-3 gap-5">
      {TOTALS.map((t) => (
        <div key={t.l} className="rounded-[2rem] bg-white p-7 text-[var(--ink)]">
          <p className="font-display text-6xl font-extrabold leading-none text-[var(--blue)]">{t.v}</p>
          <p className="mt-3 text-base font-semibold text-[var(--ink)]/70">{t.l}</p>
        </div>
      ))}
    </div>
    <p className="mt-8 max-w-2xl text-lg text-white/85">
      Três canais no YouTube e dois perfis no Instagram: entretenimento seguro para a criançada e tranquilidade para os pais.
    </p>
  </Slide>
);

const TONE = { green: ["bg-[var(--green)]", "var(--green-deep)"], blue: ["bg-[var(--blue)]", "var(--blue-deep)"] };

export const Channels = (p) => (
  <Slide {...p} testid="slide-canais">
    <SlideTitle kicker="Nossos canais & redes">Aperte o play e venha com a gente.</SlideTitle>
    <div className="mt-8 grid grid-cols-12 gap-5">
      {YT_CHANNELS.map((c) => (
        <div key={c.key} className={`stitch col-span-4 flex flex-col rounded-[2rem] p-6 text-white ${TONE[c.color][0]}`} style={{ boxShadow: `0 8px 0 ${TONE[c.color][1]}` }}>
          <span className="inline-flex w-fit items-center gap-2 rounded-full bg-white px-3 py-1 text-[11px] font-bold text-[var(--red)]">
            <Youtube className="h-3.5 w-3.5" /> YouTube
          </span>
          <img src={c.logo} alt={c.title} className="mx-auto my-4 h-28 w-auto -rotate-2" />
          <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-white/85">{c.tag}</p>
          <h3 className="font-display text-2xl font-extrabold leading-tight">{c.handle}</h3>
          <dl className="mt-4 grid grid-cols-3 gap-2 border-t-2 border-dashed border-white/40 pt-3">
            {c.stats.map((s) => (
              <div key={s.l}>
                <dt className="font-display text-xl font-extrabold leading-none">{s.v}</dt>
                <dd className="mt-1 text-[10px] text-white/85">{s.l}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
      <div className="col-span-4 flex flex-col gap-5">
        <div className="felt flex flex-1 items-center gap-4 rounded-[2rem] bg-[var(--ink)] p-5 text-white">
          <img src={LOGOS.games} alt="Pedro Clube Games" className="h-20 w-auto -rotate-3" />
          <span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--yellow)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[var(--ink)]">
              <Youtube className="h-3.5 w-3.5" /> YouTube · Games
            </span>
            <span className="font-display mt-1 block text-xl font-extrabold">@pedroclubegames</span>
          </span>
        </div>
        {IG_CHANNELS.map((c) => (
          <div key={c.key} className="sticker flex items-center gap-4 rounded-[1.75rem] bg-white p-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white">
              <Instagram className="h-7 w-7" />
            </span>
            <span className="min-w-0">
              <span className="font-mono-label block text-[10px] uppercase tracking-[0.18em] text-[var(--ink)]/65">Instagram · {c.tag}</span>
              <span className="font-display block text-lg font-extrabold leading-tight text-[var(--ink)]">{c.handle}</span>
            </span>
          </div>
        ))}
      </div>
    </div>
  </Slide>
);

export const About = (p) => (
  <Slide {...p} bg="bg-white" testid="slide-sobre">
    <div className="grid h-full grid-cols-12 items-center gap-12">
      <div className="col-span-6">
        <SlideTitle kicker="Sobre nós">
          Uma família <span className="puffy puffy-green">de verdade</span>, criando juntos.
        </SlideTitle>
        <p className="mt-6 text-lg text-[var(--ink)]/75">
          Somos a família Diego, Dani e Pedro, de Porto Alegre (RS). O papai Diego é publicitário e a mamãe Dani é administradora. Nossa paixão é criar memórias, músicas e entretenimento de valor para toda a família, com conteúdo seguro, autêntico e de alta qualidade.
        </p>
        <ul className="mt-6 grid grid-cols-3 gap-3">
          {FAMILY.map((m) => (
            <li key={m.key} className="rounded-2xl border-2 border-[var(--ink)]/10 bg-[var(--cream)] p-4">
              <span className="block h-1.5 w-8 rounded-full" style={{ background: m.color }} />
              <span className="font-display mt-2 block text-2xl font-extrabold leading-none">{m.name}</span>
              <span className="mt-1 block text-sm font-medium text-[var(--ink)]/75">{m.role}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="relative col-span-6 h-[520px]">
        <figure className="absolute left-0 top-4 w-[78%] -rotate-2 rounded-[1.6rem] bg-white p-3 shadow-[0_30px_60px_-25px_rgba(27,27,51,0.5)]">
          <div className="aspect-[16/10] overflow-hidden rounded-2xl">
            <img src={PHOTOS.familia} alt="Diego, Dani e Pedro" className="h-full w-full object-cover object-[50%_20%]" />
          </div>
          <figcaption className="font-display px-2 pt-2 text-lg font-bold">Diego, Dani & Pedro</figcaption>
        </figure>
        <figure className="absolute bottom-4 right-0 w-[34%] rotate-[5deg] rounded-[1.4rem] bg-white p-2.5 shadow-[0_30px_50px_-20px_rgba(27,27,51,0.55)]">
          <div className="aspect-[3/4] overflow-hidden rounded-xl">
            <img src={PHOTOS.pedro} alt="Pedro" className="h-full w-full object-cover object-top" />
          </div>
          <figcaption className="font-display px-1 pt-1.5 text-base font-bold text-[var(--red)]">Pedro ★</figcaption>
        </figure>
        <span className="absolute bottom-10 left-4 inline-flex -rotate-3 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-sm font-bold text-white shadow-lg">
          <MapPin className="h-4 w-4 text-[var(--yellow)]" /> {CITY}
        </span>
        <Star className="absolute -top-2 right-[28%] h-10 w-10" />
      </div>
    </div>
  </Slide>
);

const AICON = { tv: Tv, female: Users, brasil: MapPin };
const ATINT = { tv: "var(--yellow)", female: "var(--red)", brasil: "var(--green)" };

export const Audience = (p) => (
  <Slide {...p} bg="bg-[var(--blue-deep)]" dark testid="slide-audiencia">
    <SlideTitle kicker="Mídia kit" dark>
      Quem nos <span className="text-[var(--yellow)]">assiste?</span>
    </SlideTitle>
    <div className="mt-8 grid grid-cols-3 gap-5" style={{ minHeight: 430 }}>
      {AUDIENCE.map((a, i) => {
        const Icon = AICON[a.key];
        return (
          <div key={a.key} className="flex flex-col justify-between rounded-[2rem] border-2 border-white/15 bg-white/[0.06] p-7">
            <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: ATINT[a.key] }}>
              <Icon className="h-6 w-6 text-[var(--ink)]" />
            </span>
            <div>
              <p className={`font-display font-extrabold leading-none ${i === 0 ? "text-8xl" : "text-7xl"}`} style={{ color: ATINT[a.key] }}>
                {a.big}
              </p>
              <h3 className="font-display mt-3 text-2xl font-bold">{a.title}</h3>
              <p className="mt-1 text-white/80">{a.text}</p>
            </div>
          </div>
        );
      })}
    </div>
  </Slide>
);

export const Videos = ({ group, ...p }) => (
  <Slide {...p} testid={`slide-videos-${group.key}`}>
    <SlideTitle kicker={`Destaques · ${group.channel}`}>
      Os vídeos que a <span className="puffy puffy-red">criançada</span> mais ama.
    </SlideTitle>
    <h3 className="font-display mt-6 text-3xl font-extrabold">
      <span className={`puffy ${group.puffy}`}>{group.label}</span>
    </h3>
    <div className="mt-5 grid grid-cols-3 gap-6">
      {group.videos.map((v) => (
        <div key={v.key}>
          <div className="sticker relative aspect-video overflow-hidden rounded-[1.5rem] bg-[var(--ink)]">
            <img src={v.img} alt={v.title} className="absolute inset-0 h-full w-full object-cover" />
            <span className="absolute left-1/2 top-1/2 grid h-14 w-14 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-white/95 shadow-xl">
              <Play className="ml-1 h-6 w-6" style={{ color: v.tint, fill: v.tint }} />
            </span>
          </div>
          <h4 className="font-display mt-3 text-xl font-extrabold leading-tight">{v.title}</h4>
          <p className="font-mono-label mt-1 text-[11px] text-[var(--ink)]/55">{v.href}</p>
        </div>
      ))}
    </div>
  </Slide>
);

const PICON = { oferecemos: Megaphone, solicitamos: Gift, contrapartida: Handshake };

export const Partnership = (p) => (
  <Slide {...p} bg="bg-white" testid="slide-parcerias">
    <SlideTitle kicker="Parceria ganha-ganha">
      Parques, hotéis e atrações: <span className="puffy puffy-blue">bora crescer juntos?</span>
    </SlideTitle>
    <p className="mt-5 max-w-3xl text-lg text-[var(--ink)]/75">
      Queremos nos conectar com marcas para criar ações e vídeos de grande interesse, gerando benefícios reais e visibilidade atemporal, levando nosso público a destinos incríveis.
    </p>
    <ol className="mt-8 grid grid-cols-3 gap-5">
      {PARTNERSHIP.map((pt, i) => {
        const Icon = PICON[pt.key];
        return (
          <li key={pt.key} className="relative flex flex-col rounded-[2rem] border-2 border-[var(--ink)]/10 bg-[var(--cream)] p-6">
            <span className="font-display absolute right-6 top-3 text-6xl font-extrabold text-[var(--ink)]/10">0{i + 1}</span>
            <span className="grid h-12 w-12 place-items-center rounded-2xl text-white" style={{ background: pt.color }}>
              <Icon className="h-6 w-6" />
            </span>
            <span className="font-mono-label mt-5 text-[11px] uppercase tracking-[0.2em] text-[var(--ink)]/65">{pt.kicker}</span>
            <h3 className="font-display mt-1 text-2xl font-extrabold">{pt.title}</h3>
            <p className="mt-2 text-[15px] leading-snug text-[var(--ink)]/75">{pt.text}</p>
          </li>
        );
      })}
    </ol>
  </Slide>
);

const LINKS = [
  { h: SOCIAL.ytShow, I: Youtube, t: "youtube.com/@pedroclubeshow" },
  { h: SOCIAL.ytFamilia, I: Youtube, t: "youtube.com/@familiapedroclubeshow" },
  { h: SOCIAL.ytGames, I: Youtube, t: "youtube.com/@pedroclubegames" },
  { h: SOCIAL.igShow, I: Instagram, t: "instagram.com/pedroclubeshow" },
  { h: SOCIAL.igFamilia, I: Instagram, t: "instagram.com/familiapedroclubeshow" },
];

export const Contact = (p) => (
  <Slide {...p} bg="bg-[var(--red)]" dark testid="slide-contato">
    <Youtube className="absolute left-[6%] top-10 h-14 w-14 text-white" strokeWidth={2.2} />
    <Star className="absolute bottom-20 right-[8%] h-16 w-16" />
    <div className="grid h-full grid-cols-12 items-center gap-10">
      <div className="col-span-7">
        <SlideTitle kicker="Contato & parcerias" dark>
          Fale com a gente!
        </SlideTitle>
        <p className="mt-4 max-w-md text-lg text-white/90">Estamos prontos para mostrar o melhor do seu espaço, parque, hotel, viagem ou produto, de forma natural e atemporal.</p>
        <div className="mt-6 grid gap-4">
          <a href={CONTACT.whatsapp} className="flex items-center gap-4 rounded-[1.75rem] bg-[#1fa855] p-4 text-white" style={{ boxShadow: "0 6px 0 #0f6e33" }}>
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20"><MessageCircle className="h-6 w-6" /></span>
            <span>
              <span className="font-mono-label block text-[11px] uppercase tracking-[0.18em] text-white/85">WhatsApp / Telefone</span>
              <span className="font-display block text-2xl font-extrabold">{CONTACT.phone}</span>
            </span>
          </a>
          <a href={CONTACT.mailto} className="flex items-center gap-4 rounded-[1.75rem] bg-[var(--blue)] p-4 text-white" style={{ boxShadow: "0 6px 0 var(--blue-deep)" }}>
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-white/20"><Mail className="h-6 w-6" /></span>
            <span>
              <span className="font-mono-label block text-[11px] uppercase tracking-[0.18em] text-white/85">E-mail comercial</span>
              <span className="font-display block text-2xl font-extrabold">{CONTACT.email}</span>
            </span>
          </a>
        </div>
        <ul className="mt-6 flex flex-wrap gap-2">
          {LINKS.map(({ h, I, t }) => (
            <li key={t}>
              <a href={h} className="inline-flex items-center gap-2 rounded-full border border-white/40 px-3 py-1.5 text-xs font-semibold text-white">
                <I className="h-3.5 w-3.5" /> {t}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-span-5 flex flex-col items-center gap-6">
        <img src={LOGOS.familia} alt="Família Pedro Clube Show" className="w-full max-w-sm -rotate-3 drop-shadow-[0_30px_40px_rgba(0,0,0,0.3)]" />
        <div className="flex items-center gap-4">
          <img src={LOGOS.show} alt="Pedro Clube Show" className="h-14 w-auto" />
          <img src={LOGOS.games} alt="Pedro Clube Games" className="h-14 w-auto" />
        </div>
      </div>
    </div>
  </Slide>
);

export const DECK = [Cover, Numbers, Channels, About, Audience, (p) => <Videos {...p} group={HIGHLIGHTS[0]} />, (p) => <Videos {...p} group={HIGHLIGHTS[1]} />, Partnership, Contact];
